import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatByTheme } from '../../../helpers/theme'
import { useCalculateSize } from '../../hooks/useCalculateSize'
import { UseTypeFontSize } from '../../hooks/useTypeFontSize'
import { deleteChartData, setChartData } from '../store'
import TooltipContent from './TooltipContent'
import { TooltipCursor } from './TooltipCursor'
import { MARGIN_CHART } from './constants'
import { getXAxisProps, getYAxisProps } from './helper'
import './index.module.scss'

export const ChartContainer = ({
  data,
  width,
  height,
  chartType,
  layout,
  chartId,
  xAxis,
  yAxis,
  children,
  tooltip,
  margin,
  cartesianGrid,
}) => {
  const dispatch = useDispatch()
  const ref = useRef()

  const locale = useSelector((state) => state.i18n.locale)
  const typeFontSize = UseTypeFontSize()
  const { width: chartWidth, height: chartHeight } = useCalculateSize(ref)

  let enableReferenceLine = false
  let referenceYAxisId = 'left'

  const yAxisProps = getYAxisProps({ data, yAxis, typeFontSize, locale })
  let xAxisProps = getXAxisProps({
    data,
    xAxis,
    locale,
    typeFontSize,
    chartWidth,
    margin: { ...MARGIN_CHART, ...margin },
    yAxisProps,
  })

  // Enable reference line when yAxis have negative values
  for (let i = 0; i < yAxisProps.length; i++) {
    if (
      Array.isArray(yAxisProps[i].ticks) &&
      Math.min(...yAxisProps[i].ticks) < 0
    ) {
      enableReferenceLine = true
      referenceYAxisId = yAxisProps[i].yAxisId
      xAxisProps = xAxisProps.map((props) => {
        if (
          props.hasOwnProperty('axisLine') &&
          typeof props.axisLine === 'object' &&
          props.axisLine.hasOwnProperty('stroke')
        ) {
          return {
            ...props,
            axisLine: { stroke: '#F3F3F3' },
          }
        }
        return props
      })
      break
    }
  }

  const renderYAxis = () => {
    return yAxisProps.map((props) => <YAxis key={props.yAxisId} {...props} />)
  }

  const renderXAxis = () => {
    return xAxisProps.map((props) => <XAxis key={props.xAxisId} {...props} />)
  }

  const renderReferenceLine = () => {
    if (!enableReferenceLine) return <></>
    return <ReferenceLine yAxisId={referenceYAxisId} y={0} stroke="#D5D5D5" />
  }

  const renderCartesianGrid = () => {
    const props = {
      stroke: formatByTheme('#F3F3F3'),
      vertical: false,
      ...cartesianGrid,
    }
    return <CartesianGrid {...props} />
  }

  const renderChildren = () => {
    return React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child, { chartId })
      }
      return child
    })
  }

  const renderTooltip = () => {
    if (!tooltip) return <></>

    let { content, schema, ...others } = tooltip

    if (schema) {
      content = <TooltipContent chartId={chartId} schema={schema} />
    }

    const cursor = chartId ? (
      <TooltipCursor chartId={chartId} />
    ) : (
      {
        stroke: formatByTheme('#555555'),
        strokeDasharray: '3 3',
      }
    )

    return (
      <Tooltip
        isAnimationActive={false}
        separator=""
        cursor={cursor}
        allowEscapeViewBox={{ x: false, y: true }}
        wrapperStyle={{ zIndex: 1 }}
        content={content}
        {...others}
      />
    )
  }

  useEffect(() => {
    if (chartId) {
      dispatch(setChartData({ chartId }))
      return () => {
        dispatch(deleteChartData({ chartId }))
      }
    }
  }, [chartId])

  const ChartWrapper = chartType || ComposedChart

  return (
    <div ref={ref} style={{ width, height }} className="positionRelative">
      <div className="positionAbsolute">
        <ChartWrapper
          width={chartWidth}
          height={chartHeight}
          data={data}
          layout={layout}
          margin={{ ...MARGIN_CHART, ...margin }}
          barGap={2}
          stackOffset="sign"
        >
          {renderCartesianGrid()}
          {renderYAxis()}
          {renderXAxis()}
          {renderReferenceLine()}
          {renderChildren()}
          {renderTooltip()}
        </ChartWrapper>
      </div>
    </div>
  )
}

ChartContainer.propTypes = {
  chartType: PropTypes.func, // Rechart types: ComposedChart, ScatterChart,...
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  cartesianGrid: PropTypes.object,
  chartId: PropTypes.string,
  yAxis: PropTypes.arrayOf(
    PropTypes.shape({
      yAxisId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      type: PropTypes.oneOf(['number', 'category']),
      dataKeys: PropTypes.array,
      orientation: PropTypes.oneOf(['left', 'right']),
      isGroupBar: PropTypes.bool,
      isStackedBar: PropTypes.bool,
      isFloatBar: PropTypes.bool,
      isLineChart: PropTypes.bool,
      isBarChart: PropTypes.bool,
      tickStyle: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
      labelText: PropTypes.string,
      labelPosition: PropTypes.string,
      lineKeyWithStackBar: PropTypes.string,
      decimalLength: PropTypes.number,
      // All rechart yAxis props
    }),
  ),
  xAxis: PropTypes.arrayOf(
    PropTypes.shape({
      xAxisId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.oneOf(['category', 'number']),
      dataKeys: PropTypes.array,
      isGroupBar: PropTypes.bool, // Useful when axis type is number
      isStackedBar: PropTypes.bool, // Useful when axis type is number
      isFloatBar: PropTypes.bool, // Useful when axis type is number
      isLineChart: PropTypes.bool, // Useful when axis type is number
      isBarChart: PropTypes.bool, // Useful when axis type is number
      tickStyle: PropTypes.object,
      labelText: PropTypes.string,
      labelPosition: PropTypes.string,
      decimalLength: PropTypes.number,
      // All rechart xAxis props
    }),
  ),
  tooltip: PropTypes.shape({
    schema: PropTypes.shape({
      title: PropTypes.shape({
        hide: PropTypes.bool,
        formatter: PropTypes.func,
      }),
      content: PropTypes.arrayOf(
        PropTypes.shape({
          dataKey: PropTypes.string.isRequired,
          label: PropTypes.string,
          formatLabel: PropTypes.func,
          formatValue: PropTypes.func,
          formatValueColor: PropTypes.func,
          unit: PropTypes.string,
        }),
      ),
    }),
    // All rechart tooltip props
  }),
}

ChartContainer.defaultProps = {
  data: [],
  width: 0,
  height: 0,
  yAxis: [],
  xAxis: [],
  margin: {},
  cartesianGrid: {},
}
